import { translate } from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType, FilterValues } from "@kaltura/mediaspace-shared-types";
import { DragEvent, useState } from "react";
import styled from "@emotion/styled";
import Collapse from "@mui/material/Collapse";
import { IconButton, Link, Typography } from "@kaltura/ds-react-components";
import { ChevronDown16Icon, Plus16Icon } from "@kaltura/ds-react-icons";
import FoldersFilters from "./folders-filters/FoldersFilters";
import { useFoldersStore } from "./foldersStore";
import { EditFolderModal } from "./edit-folder-modal/EditFolderModal";
import FoldersFiltersSkeleton from "./folders-filters/FoldersFiltersSkeleton";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface VerticalFoldersFilterBarProps {
    /** currently selected filter values */
    filterValues: FilterValues;
    /** callback to execute when selecting a folder id filter */
    onChange: (values: FilterValues) => void;
}

const StyledFilterWrapper = styled(
    "div",
    {
        shouldForwardProp: (prop) => prop !== "isExpanded",
    }
)<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
    borderBottom: isExpanded ? "none" : `1px solid ${theme.kaltura.palette.tone6}`,
    display: "flex",
    alignItems: "center",
}));

const StyledLabelButton = styled(Link)(({ theme }) => ({
    flexGrow: 1,
    textAlign: "left",
    textDecoration: "none",
    padding: theme.spacing(2, 0),
    "&:hover, &:active, &:focus": {
        color: theme.kaltura.palette.tone1,
    },
}));

const StyledActionsSection = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
}));

// we don't want this "button" to be focused by keyboard
// StyledLabelButton is controlling the expansion for keyboard users
// however, we do want to toggle expansion when the icon is clicked with the mouse
const StyledExpandIcon = styled(
    ChevronDown16Icon,
    {
        shouldForwardProp: (prop) => prop !== "isExpanded",
    }
)<{ isExpanded: boolean }>(({ isExpanded }) => ({
    transform: isExpanded ? "rotate(180deg)" : "none",
    transition: "transform 0.2s",
    cursor: "pointer",
}));

const StyledFiltersContent = styled("div")(({ theme }) => ({
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.kaltura.palette.tone6}`,
}));

const baseClassName = "kms-ds-folders-vertical-filter-bar";

export function VerticalFoldersFilterBar({ filterValues, onChange }: VerticalFoldersFilterBarProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const isLoadingFolders = useFoldersStore((state) => state.isLoadingFolders);

    const sendButtonAnalytics = useButtonAnalytics();

    // expand when dragging my-media search results over wrapper
    const onDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsExpanded(true);
    };

    const handleOpenCreateModal = () => {
        setIsCreateModalOpen(true);
        sendButtonAnalytics("Create folder", ButtonClickAnalyticsType.CREATE, undefined, "folders");
    };

    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    const selectedFolderId = typeof filterValues.folderId === "string" ? filterValues.folderId : "";

    const toggleIsExpanded = () => {
        sendButtonAnalytics(
            `Toggle folder tree - ${isExpanded ? "collapse" : "expand"}`,
            ButtonClickAnalyticsType.TOGGLE,
            undefined,
            "folders"
        );
        setIsExpanded(!isExpanded);
    };

    return (
        <ButtonAnalyticsProvider applicationFeature={"folders"}>
            <div className={`${baseClassName}-container`}>
                <StyledFilterWrapper isExpanded={isExpanded} onDragOver={onDragOver}>
                    <StyledLabelButton
                        component={"button"}
                        onClick={toggleIsExpanded}
                        aria-expanded={isExpanded}
                        aria-controls={"folders-filters-content"}
                        className={`${baseClassName}-expand-button`}
                    >
                        <Typography variant={"body2Highlight"} className={`${baseClassName}-header`}>
                            {translate("My folders")}
                        </Typography>
                    </StyledLabelButton>
                    <StyledActionsSection className={`${baseClassName}-actions-section`}>
                        <IconButton
                            color={"tone1"}
                            size={"small"}
                            aria-label={translate("Create folder")}
                            className={`${baseClassName}-create-folder`}
                            onClick={handleOpenCreateModal}
                            disabled={isLoadingFolders}
                        >
                            <Plus16Icon />
                        </IconButton>
                        <StyledExpandIcon isExpanded={isExpanded} onClick={toggleIsExpanded} />
                    </StyledActionsSection>
                </StyledFilterWrapper>
                <Collapse in={isExpanded}>
                    <StyledFiltersContent id={"folders-filters-content"} className={`${baseClassName}-filters-content`}>
                        {isLoadingFolders ? (
                            <FoldersFiltersSkeleton />
                        ) : (
                            <FoldersFilters selectedFolderId={selectedFolderId} onChange={onChange} />
                        )}
                    </StyledFiltersContent>
                </Collapse>
                <ButtonAnalyticsProvider location={"Modal - Create folder"} applicationFeature={"folders"}>
                    {isCreateModalOpen && (
                        <EditFolderModal
                            isOpen={isCreateModalOpen}
                            onClose={handleCloseCreateModal}
                            onSubmitEnd={handleCloseCreateModal}
                        />
                    )}
                </ButtonAnalyticsProvider>
            </div>
        </ButtonAnalyticsProvider>
    );
}

export default VerticalFoldersFilterBar;
